// Package imports
import React, { useCallback, useEffect, useRef, useState } from 'react';
import EmailEditor, { EditorRef, EmailEditorProps } from 'react-email-editor';
import { useNavigate, useParams } from 'react-router-dom';

// CSS imports
import './UpdateNewsletterEmail.css';

// Component imports
import TextInput from '../../components/inputs/textInput/TextInput';
import PrimaryButton from '../../components/buttons/primaryButton/PrimaryButton';
import FullPageSpinner from '../../components/fullPageSpinner/FullPageSpinner';
import Alert from '../../components/alert/Alert';

// Helper imports
import SectionWrapper from '../../helpers/components/sectionWrapper/SectionWrapper';

// Service imports
import { ApiService } from '../../services/ApiService';

// Icon imports
import { MdError } from 'react-icons/md';

// Type imports
import { AlertSize } from '../../types/alert/AlertSize';
import { NewsletterEmail } from '../../types/newsletter/NewsletterEmail';

const UpdateNewsletterEmail: React.FC = () => {
  const [emailId, setEmailId] = useState('');
  const [emailDesign, setEmailDesign] = useState<any>();
  const [name, setName] = useState('');
  const [subject, setSubject] = useState('');
  const [nameError, setNameError] = useState('');
  const [subjectError, setSubjectError] = useState('');

  const [isLoading, setIsLoading] = useState(false);
  const [isPageLoading, setIsPageLoading] = useState(true);
  const [showUnknownError, setShowUnknownError] = useState(false);

  const emailEditorRef = useRef<EditorRef>(null);
  const navigate = useNavigate();
  let { id } = useParams();

  const getEmail = useCallback(async () => {
    if (id) {
      const res: NewsletterEmail | null = await ApiService.getNewsletterEmail(
        id
      );

      if (res) {
        setName(res.name);
        setSubject(res.subject);
        setEmailId(res.id);
        setEmailDesign(res.design);
      }

      setIsPageLoading(false);
    } else {
      setIsPageLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (emailId === '') {
      getEmail();
    } else {
      setIsPageLoading(false);
    }
  }, [emailId, getEmail]);

  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    switch (e.target.name) {
      case 'name': {
        setNameError('');
        setName(e.target.value);
        break;
      }
      case 'subject': {
        setSubjectError('');
        setSubject(e.target.value);
        break;
      }
    }
  };

  const triggerSave = () => {
    const isValid = checkInputs();

    if (isValid) {
      save();
    }
  };

  const save = async () => {
    const unlayer = emailEditorRef.current?.editor;

    unlayer?.exportHtml(async (data) => {
      const { design, html } = data;
      setIsLoading(true);

      const res: boolean = await ApiService.updateNewsletterEmail(
        emailId,
        name,
        subject,
        html,
        design
      );

      if (res) {
        navigate('/newsletter-emails');
      } else {
        setShowUnknownError(true);
      }
    });
  };

  const checkInputs = () => {
    let isValid = true;

    if (name === '') {
      setNameError('Please enter a name');
      isValid = false;
    }

    if (subject === '') {
      setSubjectError('Please enter a subject');
      isValid = false;
    }

    return isValid;
  };

  const onReady: EmailEditorProps['onReady'] = (unlayer) => {
    unlayer.loadDesign(emailDesign);
  };

  return (
    <div className='UpdateNewsletterEmail'>
      <SectionWrapper>
        <div className='content'>
          <h1>Update Email</h1>
          {!isPageLoading && emailId !== '' ? (
            <>
              <div className='save-button-container'>
                <PrimaryButton label='Save' onClick={triggerSave} />
              </div>
              <TextInput
                value={name}
                name='name'
                label='Name'
                onChange={onChange}
                errorMessage={nameError}
                className='name-input'
              />
              <TextInput
                value={subject}
                name='subject'
                label='Subject'
                onChange={onChange}
                errorMessage={subjectError}
                className='subject-input'
              />
              <EmailEditor ref={emailEditorRef} onReady={onReady} />
            </>
          ) : null}
          {!isPageLoading && emailId === '' ? <div>Email not found</div> : null}
        </div>
      </SectionWrapper>
      {isLoading ? <FullPageSpinner /> : null}
      <Alert
        showAlert={showUnknownError}
        size={AlertSize.SMALL}
        time={3000}
        setShowAlert={setShowUnknownError}
      >
        <MdError size={20} />
        <span>Something went wrong!</span>
      </Alert>
    </div>
  );
};

export default UpdateNewsletterEmail;
